/*
 * This file is part of the Akershus Eiendom Nettsted 2019 application.
 *
 * (c) APT AS
 *
 * For the full copyright and license information, please view the LICENSE
 * file that was distributed with this source code.
 */

import React from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames';
import Collapsible from 'components/Collapsible/Collapsible';
import './BooleanList.scss';

/**
 * This is the BooleanList component.
 *
 * @author Thomas Sømoen <thomas@apt.no>
 *
 * @return {JSX}
 */
function BooleanList({ id, value, options, onChange, ...props }) {
  function renderCheckbox({ id, value, label }) {
    return (
      <div
        key={id}
        className={classnames('field-input field-type-checkbox checkbox', {
          active: value,
        })}
      >
        <input
          {...props}
          id={id}
          type="checkbox"
          checked={value}
          onChange={e => {
            onChange({
              ...e,
              target: {
                ...e.target,
                id,
                value: e.target.checked,
              },
            });
          }}
        />
        <label htmlFor={id} dangerouslySetInnerHTML={{ __html: label }} />
      </div>
    );
  }

  function renderOptions() {
    return (
      <Collapsible open={!!value}>
        <div className="options">{options.map(renderCheckbox)}</div>
      </Collapsible>
    );
  }

  return (
    <div className="boolean-list">
      {renderCheckbox({ ...props, id, value })}
      {renderOptions()}
    </div>
  );
}

/**
 * Declare expected prop types.
 *
 * @type {Object}
 */
BooleanList.propTypes = {
  id: PropTypes.string.isRequired,
  value: PropTypes.bool,
  options: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string.isRequired,
      label: PropTypes.string.isRequired,
      value: PropTypes.bool,
    })
  ).isRequired,
  onChange: PropTypes.func.isRequired,
};

/**
 * Declare defaults for non-required props.
 *
 * @type {Object}
 */
BooleanList.defaultProps = {
  value: false,
  options: [],
  onChange: () => {},
};

export default BooleanList;
